// Buy a liability — the "fun stuff" that costs cash to own.
// Per ECONOMY §6: liabilities aren't banned, they raise Happiness.
// The teaching arc is "buy assets first, let assets pay for liabilities."

import type { GameState } from '../state/types.js';
import type { LiabilityDef } from '../config/types.js';
import { applyTxn } from '../ledger/append.js';

export type BuyLiabilityError = { code: 'INSUFFICIENT_CASH' | 'UNKNOWN_LIABILITY'; message: string };

export type BuyLiabilityOutcome = { state: GameState; instanceId: string };

export function buyLiability(
  state: GameState,
  def: LiabilityDef,
  instanceId: string,
): { ok: true; value: BuyLiabilityOutcome } | { ok: false; error: BuyLiabilityError } {
  if (state.wallet.cash < def.cost) {
    return {
      ok: false,
      error: {
        code: 'INSUFFICIENT_CASH',
        message: `Need B$${def.cost} to buy ${def.name}, have B$${state.wallet.cash.toFixed(2)}.`,
      },
    };
  }

  const next = applyTxn(state, {
    type: 'liability_buy',
    amount: -def.cost,
    category: 'capital',
    note: `Bought ${def.name}`,
  });

  const happiness = Math.min(100, next.profile.happiness + def.happinessGain);

  return {
    ok: true,
    value: {
      state: {
        ...next,
        profile: { ...next.profile, happiness },
        liabilities: [
          ...next.liabilities,
          {
            id: instanceId,
            defId: def.id,
            costPaid: def.cost,
            weeklyUpkeep: def.weeklyUpkeep,
            weeksOwned: 0,
          },
        ],
      },
      instanceId,
    },
  };
}
